// 감사 로그 기간 선택 — 두 콘솔이 **같은 부품**을 쓴다.
//
// ⚠️ 왜 기본이 '전체'가 아닌가: 기간을 비우면 서버가 표 전체를 훑는다. audit_log 는
//    보존기간까지 계속 자라므로 그 조회는 시간이 갈수록 느려지고, 전체 건수 COUNT 도
//    같이 커진다(AuditQueryService.TOTAL_CAP 주석). 그래서 **최근 30일**로 시작한다.
//    전체를 봐야 할 때는 '전체'를 고를 수 있게 남겨 둔다 — 막지는 않는다.
//
// ⚠️ 날짜만 받고 시각은 받지 않는다. 운영자가 "어제부터"를 고를 때 시각까지 정하게
//    하면 실수만 늘어난다. 끝 날짜는 **그 날을 포함**하도록 다음 날 0시로 보낸다
//    (서버가 to 를 `< to` 로 쓴다 — AuditQueryService).
import { useId } from "react";

/** 사전 정의 구간. days=null 은 '전체'(기간 조건 없음) */
const PRESETS: ReadonlyArray<{ key: string; label: string; days: number | null }> = [
  { key: "7", label: "최근 7일", days: 7 },
  { key: "30", label: "최근 30일", days: 30 },
  { key: "90", label: "최근 90일", days: 90 },
  { key: "all", label: "전체", days: null },
];

export type AuditRange = {
  /** 사전 구간 키, 또는 "custom" (from·to 를 직접 지정) */
  preset: string;
  /** yyyy-MM-dd, preset === "custom" 일 때만 쓴다 */
  from: string;
  to: string;
};

export const DEFAULT_AUDIT_RANGE: AuditRange = { preset: "30", from: "", to: "" };

const iso = (d: Date) => d.toISOString();

/** yyyy-MM-dd 를 **그 지역의** 0시로 읽는다. UTC 로 읽으면 한국에서 9시간 밀린다. */
function localMidnight(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * 서버로 보낼 from·to (ISO). 조건이 없으면 빈 객체.
 *
 * ⚠️ **날짜 계산은 여기 한 곳에만 둔다.** 두 콘솔의 API 계층이 서로 다르게 조립하면
 *    "운영자 화면에서는 되는데 사용자 화면에서는 하루 밀린다"가 된다. 실제로 두
 *    화면이 호출 방식이 다르다(운영자는 URLSearchParams, 사용자는 질의 조각) —
 *    그래서 **값을 주는 함수**를 정본으로 두고 조각은 그 위에 얹는다.
 */
export function auditRangeParams(range: AuditRange): { from?: string; to?: string } {
  if (range.preset === "custom") {
    const out: { from?: string; to?: string } = {};
    const from = localMidnight(range.from);
    if (from) out.from = iso(from);
    const to = localMidnight(range.to);
    if (to) {
      // 끝 날짜를 포함시킨다 — 서버는 `< to` 로 자른다
      to.setDate(to.getDate() + 1);
      out.to = iso(to);
    }
    return out;
  }
  const days = PRESETS.find((p) => p.key === range.preset)?.days ?? null;
  if (days === null) return {};
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { from: iso(from) };
}

/** 위 값을 `&from=…&to=…` 질의 조각으로. 조건이 없으면 빈 문자열. */
export function auditRangeToQuery(range: AuditRange): string {
  return Object.entries(auditRangeParams(range))
    .map(([k, v]) => `&${k}=${encodeURIComponent(v)}`)
    .join("");
}

/** 화면에 보여줄 기간 설명 — 무엇을 보고 있는지 숫자 옆에 적어 준다 */
export function auditRangeSummary(range: AuditRange): string {
  if (range.preset === "custom") {
    if (!range.from && !range.to) return "전체 기간";
    return `${range.from || "처음"} ~ ${range.to || "지금"}`;
  }
  return PRESETS.find((p) => p.key === range.preset)?.label ?? "";
}

export function AuditRangePicker({
  value,
  onChange,
}: {
  value: AuditRange;
  onChange: (next: AuditRange) => void;
}) {
  const fromId = useId();
  const toId = useId();

  return (
    <div className="audit-range">
      {/* 자주 쓰는 구간은 칩으로 — 날짜를 두 번 고르지 않아도 되게 한다 */}
      <div className="filter-row" role="group" aria-label="기간">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={value.preset === p.key ? "chip chip--on" : "chip"}
            aria-pressed={value.preset === p.key}
            onClick={() => onChange({ ...value, preset: p.key })}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          className={value.preset === "custom" ? "chip chip--on" : "chip"}
          aria-pressed={value.preset === "custom"}
          onClick={() => onChange({ ...value, preset: "custom" })}
        >
          직접 지정
        </button>
      </div>

      {/* 직접 지정을 고르기 전에는 날짜 칸을 띄우지 않는다 — 쓰지 않을 입력이
          늘 자리를 차지하면 화면이 복잡해 보인다. */}
      {value.preset === "custom" && (
        <div className="audit-range__dates">
          <div className="field">
            <label htmlFor={fromId}>시작일</label>
            <input
              id={fromId}
              type="date"
              value={value.from}
              max={value.to || undefined}
              onChange={(e) => onChange({ ...value, from: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor={toId}>종료일 (포함)</label>
            <input
              id={toId}
              type="date"
              value={value.to}
              min={value.from || undefined}
              onChange={(e) => onChange({ ...value, to: e.target.value })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
